import { parseCsvRecords } from './parseCsvRow';

export type FileType =
  | 'flashlight_benchmark'
  | 'wida_access'
  | 'alternate_access_wida'
  | 'state_assessment'
  | 'student_roster'
  | 'sis';

export type ExtractedRow = {
  rowIdx: number;
  stateStudentId: string;
  districtStudentId: string;
  firstName: string;
  lastName: string;
  dob: string;
  school: string;
};

export type PerFileWarning = { code: 'missing_id_column' };

export type ExtractResult = {
  rows: ExtractedRow[];
  warnings: PerFileWarning[];
};

type ColumnSpec = {
  stateIdCandidates: string[];
  districtIdCandidates: string[];
};

const COMMON_STATE_ID = ['state student id', 'state id'];
const COMMON_DISTRICT_ID = ['district student id', 'local student id', 'student number'];
const GENERIC_ID = ['student id'];

const FIRST_NAME_CANDIDATES = ['first name', 'student first name'];
const LAST_NAME_CANDIDATES = ['last name', 'student last name'];
const DOB_CANDIDATES = ['date of birth', 'birth date', 'dob'];
const SCHOOL_CANDIDATES = ['school', 'school name', 'site', 'site name', 'campus'];
const ENROLLMENT_SOURCES: ReadonlySet<FileType> = new Set(['student_roster', 'sis']);

const SPECS: Record<FileType, ColumnSpec> = {
  wida_access: {
    stateIdCandidates: COMMON_STATE_ID,
    districtIdCandidates: [...COMMON_DISTRICT_ID, 'local id', 'unique drc student id'],
  },
  alternate_access_wida: {
    stateIdCandidates: COMMON_STATE_ID,
    districtIdCandidates: [...COMMON_DISTRICT_ID, 'local id', 'unique drc student id'],
  },
  flashlight_benchmark: {
    stateIdCandidates: COMMON_STATE_ID,
    districtIdCandidates: COMMON_DISTRICT_ID,
  },
  state_assessment: {
    stateIdCandidates: COMMON_STATE_ID,
    districtIdCandidates: [...COMMON_DISTRICT_ID, ...GENERIC_ID],
  },
  student_roster: {
    stateIdCandidates: COMMON_STATE_ID,
    districtIdCandidates: [...COMMON_DISTRICT_ID, ...GENERIC_ID],
  },
  sis: {
    stateIdCandidates: COMMON_STATE_ID,
    districtIdCandidates: [...COMMON_DISTRICT_ID, ...GENERIC_ID],
  },
};

function normalize(s: string): string {
  return s.trim().toLowerCase();
}

function findColumnIndex(normalizedHeaders: string[], candidates: string[]): number {
  for (const candidate of candidates) {
    const idx = normalizedHeaders.indexOf(candidate);
    if (idx >= 0) return idx;
  }
  return -1;
}

export type ColumnOverrides = {
  districtIdIndex?: number;
  stateIdIndex?: number;
  schoolIndex?: number;
};

export function extractStudentsFromText(
  csvText: string,
  fileType: FileType,
  overrides?: ColumnOverrides
): ExtractResult {
  const records = parseCsvRecords(csvText);
  if (records.length === 0) return { rows: [], warnings: [] };

  const headers = (records[0] ?? []).map(normalize);
  const spec = SPECS[fileType];

  const overrideIdIndex = overrides?.districtIdIndex;
  const overrideStateIndex = overrides?.stateIdIndex;
  const overrideIdIsStateId =
    overrideStateIndex == null &&
    overrideIdIndex != null &&
    spec.stateIdCandidates.includes(headers[overrideIdIndex] ?? '');
  const stateIdx =
    overrideStateIndex != null
      ? overrideStateIndex
      : overrideIdIsStateId
        ? overrideIdIndex
        : overrideIdIndex != null
          ? -1
          : findColumnIndex(headers, spec.stateIdCandidates);
  const districtIdx = overrideIdIsStateId
    ? -1
    : overrideIdIndex != null
      ? overrideIdIndex
      : overrideStateIndex != null
        ? -1
        : findColumnIndex(headers, spec.districtIdCandidates);
  const firstIdx = findColumnIndex(headers, FIRST_NAME_CANDIDATES);
  const lastIdx = findColumnIndex(headers, LAST_NAME_CANDIDATES);
  const dobIdx = findColumnIndex(headers, DOB_CANDIDATES);
  const schoolIdx = overrides
    ? (overrides.schoolIndex ?? -1)
    : ENROLLMENT_SOURCES.has(fileType)
      ? findColumnIndex(headers, SCHOOL_CANDIDATES)
      : -1;

  const warnings: PerFileWarning[] = [];
  if (stateIdx < 0 && districtIdx < 0) {
    warnings.push({ code: 'missing_id_column' });
  }

  const valueAt = (cols: string[], idx: number): string => (idx >= 0 ? (cols[idx] ?? '').trim() : '');

  const rows: ExtractedRow[] = [];
  for (let i = 1; i < records.length; i++) {
    const cols = records[i] ?? [];
    const stateStudentId = valueAt(cols, stateIdx);
    const districtStudentId = valueAt(cols, districtIdx);
    const firstName = valueAt(cols, firstIdx);
    const lastName = valueAt(cols, lastIdx);
    const dob = valueAt(cols, dobIdx);
    if (stateStudentId === '' && districtStudentId === '') {
      if (firstName === '' || lastName === '' || dob === '') continue;
    }
    rows.push({
      rowIdx: i - 1,
      stateStudentId,
      districtStudentId,
      firstName,
      lastName,
      dob,
      school: valueAt(cols, schoolIdx),
    });
  }

  return { rows, warnings };
}
